import { IntegrationConfig, TelemetryProviderEntry } from "../types";

export type TelemetryEntry = TelemetryProviderEntry;

export function normalizeTelemetryProvider(provider?: string): string {
  return (provider || "").trim().toLowerCase();
}

export function isPushTelemetryProvider(provider?: string): boolean {
  const normalized = normalizeTelemetryProvider(provider);
  return normalized === "cpi" || normalized === "cantaloupe";
}

export function telemetryProviderRequiresSecret(provider?: string): boolean {
  return !isPushTelemetryProvider(provider);
}

export function telemetryProviderCanCreateWithoutCredential(
  provider?: string
): boolean {
  const normalized = normalizeTelemetryProvider(provider);
  return normalized === "nayax" || isPushTelemetryProvider(normalized);
}

export function resolveBaseTelemetry(
  config: IntegrationConfig
): TelemetryEntry[] {
  if (config.telemetryProviders?.length) return config.telemetryProviders;
  const legacyProvider = (config as any).telemetryProvider;
  const legacyKey = (config as any).telemetryApiKey;
  if (legacyProvider || legacyKey) {
    return [{ provider: legacyProvider, apiKey: legacyKey }];
  }
  return [];
}

export function updateTelemetryList(
  list: TelemetryEntry[],
  idx: number,
  updates: TelemetryEntry
): TelemetryEntry[] {
  const next = [...list];
  next[idx] = { ...next[idx], ...updates };
  return next;
}

export function addTelemetryEntry(list: TelemetryEntry[]): TelemetryEntry[] {
  return [...list, { provider: "", apiKey: "" }];
}

export function isBlankTelemetryEntry(entry?: TelemetryEntry): boolean {
  if (!entry) return true;
  return (
    !entry.id &&
    !normalizeTelemetryProvider(entry.provider) &&
    !(entry.apiKey || "").trim() &&
    !entry.hasSecret &&
    Number(entry.cardTransactionSurchargeCents || 0) === 0 &&
    !(entry.methods || []).length &&
    !(entry.methodConfigUpdates || []).length
  );
}

export function telemetrySaveEntries(
  list: TelemetryEntry[] = []
): TelemetryEntry[] {
  return list.filter((entry) => !isBlankTelemetryEntry(entry));
}

export function removeTelemetryEntry(
  list: TelemetryEntry[],
  idx: number
): TelemetryEntry[] {
  const next = list.filter((_, i) => i !== idx);
  return next.length ? next : [{ provider: "", apiKey: "" }];
}

export function telemetryEqual(
  a: TelemetryEntry[] = [],
  b: TelemetryEntry[] = []
): boolean {
  if (a.length !== b.length) return false;
  return a.every((entry, idx) => {
    const other = b[idx] || {};
    const otherMethods = new Map(
      (other.methods || []).map((method) => [method.id, method])
    );
    const entryMethods = entry.methods || [];
    const otherMethodIds = new Set(
      (other.methods || []).map((method) => method.id)
    );
    const methodsEqual =
      entryMethods.length === otherMethodIds.size &&
      entryMethods.every((method) => {
        const otherMethod = otherMethods.get(method.id);
        return (
          !!otherMethod &&
          method.enabled === otherMethod.enabled &&
          method.status === otherMethod.status
        );
      });
    return (
      (entry.provider || "") === (other.provider || "") &&
      (entry.apiKey || "") === (other.apiKey || "") &&
      !!entry.hasSecret === !!other.hasSecret &&
      Number(entry.cardTransactionSurchargeCents || 0) ===
        Number(other.cardTransactionSurchargeCents || 0) &&
      !(entry.methodConfigUpdates || []).length &&
      methodsEqual
    );
  });
}
